// Admin → Equipment Data Recovery
// Click-driven UI for the production incident where an office employee
// created Equipment records inside the "Equipment Categories" section
// and deleted the baseline category list.
//
// Two big buttons:
//   1) "Preview What I Will Do"  → safe, read-only, shows the migration plan
//   2) "Run Recovery Now"        → writes (only enabled after a preview)
//
// Behind the scenes this calls:
//   GET  /api/equipment/admin/recover-data/preview
//   POST /api/equipment/admin/recover-data?dry_run=true|false

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Eye,
  Play,
  PackageOpen,
  FolderTree,
} from 'lucide-react';import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminEquipmentRecovery() {
  const [audit, setAudit] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [loadingDry, setLoadingDry] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  // Step-4 (fix-up) state — move stranded `db.equipment` rows into `db.assets`
  // so they actually show up on the Equipment page.
  const [moveDry, setMoveDry] = useState(null);
  const [moveLoadingDry, setMoveLoadingDry] = useState(false);
  const [moveCommitting, setMoveCommitting] = useState(false);
  const [confirmMove, setConfirmMove] = useState(false);
  const [moveResult, setMoveResult] = useState(null);

  // Step-5 (seed maintenance fields) state — re-add custom_fields to
  // categories whose original schemas were lost in the early recovery runs.
  const [seedPreview, setSeedPreview] = useState(null);
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedSelections, setSeedSelections] = useState({}); // catName -> boolean
  const [seedApplying, setSeedApplying] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('fieldflow_token')}` },
  });

  const loadAudit = async () => {
    setLoadingAudit(true);
    try {
      const r = await axios.get(`${API}/equipment/admin/recover-data/preview`, authHeaders());
      setAudit(r.data);
    } catch (e) {
      toast.error(`Audit failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoadingAudit(false);
    }
  };

  useEffect(() => { loadAudit(); }, []);

  const runDryRun = async () => {
    setLoadingDry(true);
    setCommitResult(null);
    try {
      const r = await axios.post(
        `${API}/equipment/admin/recover-data?dry_run=true`,
        {},
        authHeaders(),
      );
      setDryRun(r.data);
      toast.success('Preview ready — review the plan below before running.');
    } catch (e) {
      toast.error(`Preview failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoadingDry(false);
    }
  };

  const runCommit = async () => {
    setCommitting(true);
    try {
      const r = await axios.post(
        `${API}/equipment/admin/recover-data?dry_run=false`,
        {},
        authHeaders(),
      );
      setCommitResult(r.data);
      toast.success(
        `Recovery complete — ${r.data.phase_a_migrate_equipment.migrated_count} equipment moved, `
        + `${r.data.phase_b_restore_categories.created_count} categories restored.`,
      );
    } catch (e) {
      toast.error(`Recovery failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setCommitting(false);
      setConfirmRun(false);
    }
  };

  // ── Step 4 handlers — move stranded `db.equipment` → `db.assets` ───
  const runMoveDryRun = async () => {
    setMoveLoadingDry(true);
    setMoveResult(null);
    try {
      const r = await axios.post(
        `${API}/equipment/admin/move-equipment-to-assets?dry_run=true`,
        null,
        authHeaders(),
      );
      setMoveDry(r.data);
      toast.success(`Preview ready — ${r.data.would_move_count} stranded record(s) to move.`);
    } catch (e) {
      toast.error(`Preview failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMoveLoadingDry(false);
    }
  };

  const runMoveCommit = async () => {
    setMoveCommitting(true);
    try {
      const r = await axios.post(
        `${API}/equipment/admin/move-equipment-to-assets?dry_run=false`,
        null,
        authHeaders(),
      );
      setMoveResult(r.data);
      toast.success(`Moved ${r.data.moved_count} record(s) into the Equipment page.`);
      // Refresh the live audit so the counters update
      await loadAudit();
    } catch (e) {
      toast.error(`Move failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMoveCommitting(false);
      setConfirmMove(false);
    }
  };

  // ── Step 5 handlers — apply standard maintenance fields ─────────────
  const loadSeedPreview = async () => {
    setSeedLoading(true);
    setSeedResult(null);
    try {
      const r = await axios.get(
        `${API}/equipment/admin/seed-standard-fields/preview`,
        authHeaders(),
      );
      setSeedPreview(r.data);
      // Default-select categories where applying WOULD actually add at least
      // one new field. Categories whose suggested fields are all already
      // present (every "Will Add" row strikethrough) are left unchecked so
      // the user doesn't accidentally re-tick a no-op row.
      const selections = {};
      (r.data.categories || []).forEach((c) => {
        if (!c.has_suggestion) return;
        const existingLc = (c.current_field_labels || []).map((s) => (s || '').toLowerCase());
        const newOnes = (c.suggested_defaults || []).filter(
          (f) => !existingLc.includes((f.label || '').toLowerCase())
        );
        if (newOnes.length > 0) {
          selections[c.name] = true;
        }
      });
      setSeedSelections(selections);
    } catch (e) {
      toast.error(`Preview failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSeedLoading(false);
    }
  };

  const applySeedFields = async () => {
    const chosen = Object.entries(seedSelections).filter(([, v]) => v).map(([k]) => k);
    if (chosen.length === 0) {
      toast.error('Pick at least one category.');
      return;
    }
    setSeedApplying(true);
    try {
      const r = await axios.post(
        `${API}/equipment/admin/seed-standard-fields?dry_run=false`,
        { category_names: chosen },
        authHeaders(),
      );
      setSeedResult(r.data);
      toast.success(`Applied maintenance fields to ${r.data.applied_count} categor${r.data.applied_count === 1 ? 'y' : 'ies'}.`);
      // Refresh the preview so the user sees updated field counts
      await loadSeedPreview();
    } catch (e) {
      toast.error(`Apply failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSeedApplying(false);
    }
  };

  // ── Step 6 handlers — consolidate duplicate categories ───────────
  const [consolDry, setConsolDry] = useState(null);
  const [consolLoading, setConsolLoading] = useState(false);
  const [consolCommitting, setConsolCommitting] = useState(false);
  const [consolResult, setConsolResult] = useState(null);

  // Hardcoded for the Feb 2026 production incident — the duplicate pairs
  // we know exist. Easy to extend later if more dupes pop up.
  const DUPLICATE_GROUPS = [
    ["Vac Trucks", "Vac Truck"],   // plural (no fields) + singular (with fields)
  ];

  const runConsolDryRun = async () => {
    setConsolLoading(true);
    setConsolResult(null);
    try {
      const r = await axios.post(
        `${API}/equipment/admin/consolidate-categories?dry_run=true`,
        { duplicate_groups: DUPLICATE_GROUPS },
        authHeaders(),
      );
      setConsolDry(r.data);
      toast.success('Consolidation preview ready.');
    } catch (e) {
      toast.error(`Preview failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setConsolLoading(false);
    }
  };

  const runConsolCommit = async () => {
    setConsolCommitting(true);
    try {
      const r = await axios.post(
        `${API}/equipment/admin/consolidate-categories?dry_run=false`,
        { duplicate_groups: DUPLICATE_GROUPS },
        authHeaders(),
      );
      setConsolResult(r.data);
      toast.success(`Consolidated ${r.data.groups_merged} duplicate group(s).`);
      await loadAudit();
    } catch (e) {
      toast.error(`Consolidation failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setConsolCommitting(false);
    }
  };

  const phaseA = dryRun?.phase_a_migrate_equipment;
  const phaseB = dryRun?.phase_b_restore_categories;
  const stranded = audit?.summary?.stranded_in_equipment_collection || 0;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="equipment-recovery-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Equipment Data Recovery</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Move equipment that was mistakenly entered into <strong>Equipment Categories</strong>
            {' '}back to the <strong>Equipment</strong> page, and restore the baseline
            categories that were deleted. Safe to run — nothing is written until you
            press <em>&ldquo;Run Recovery Now&rdquo;</em>.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadAudit} disabled={loadingAudit}
                data-testid="refresh-audit-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loadingAudit ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* ── Step 1: Current state audit ─────────────────────────── */}
      <Card data-testid="audit-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600" />
            Step 1 · Current state
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!audit ? (
            <p className="text-sm text-muted-foreground">Loading audit…</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Rows in Equipment Categories"
                    value={audit.summary.total_in_equipment_categories}
                    testid="stat-total-cats" />
              <Stat label="Misplaced equipment found"
                    value={audit.summary.misplaced_equipment_in_categories}
                    tone={audit.summary.misplaced_equipment_in_categories > 0 ? 'warn' : 'ok'}
                    testid="stat-misplaced" />
              <Stat label="Proper categories"
                    value={audit.summary.proper_categories_in_categories}
                    testid="stat-proper" />
              <Stat label="Rows in Equipment"
                    value={audit.summary.total_in_equipment_collection}
                    testid="stat-equipment" />
            </div>
          )}
          {audit?.misplaced_equipment_preview?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <PackageOpen className="w-4 h-4 text-amber-600" />
                Misplaced equipment in Categories ({audit.misplaced_equipment_preview.length})
              </h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-2">Name (in Categories now)</th>
                      <th className="p-2">Description</th>
                      <th className="p-2 text-right">Custom Fields</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.misplaced_equipment_preview.map((r) => (
                      <tr key={r.id} className="border-t">
                        <td className="p-2 font-medium">{r.name}</td>
                        <td className="p-2 text-muted-foreground">{r.description || '—'}</td>
                        <td className="p-2 text-right">{r.custom_field_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Step 2: Dry run preview ─────────────────────────────── */}
      <Card data-testid="dryrun-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderTree className="w-5 h-5 text-indigo-600" />
            Step 2 · Preview what will change
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This is a <strong>dry run</strong> — nothing is saved. It shows you exactly
            which records will be moved and which baseline categories will be recreated.
          </p>
          <Button onClick={runDryRun} disabled={loadingDry} data-testid="run-dryrun-btn">
            <Eye className="w-4 h-4 mr-2" />
            {loadingDry ? 'Generating preview…' : 'Preview What I Will Do'}
          </Button>

          {dryRun && (
            <div className="space-y-4 pt-3">
              {/* Phase A */}
              <div>
                <h3 className="text-sm font-semibold mb-2">
                  Equipment to move into the Equipment page
                  {' '}<Badge variant="outline">{phaseA.candidates.length}</Badge>
                </h3>
                {phaseA.candidates.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    Nothing to migrate — categories collection looks clean.
                  </p>
                ) : (
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 text-left">
                        <tr>
                          <th className="p-2">Equipment Name</th>
                          <th className="p-2">New Category</th>
                          <th className="p-2">Year</th>
                          <th className="p-2">Make</th>
                          <th className="p-2">Model</th>
                          <th className="p-2">Serial</th>
                        </tr>
                      </thead>
                      <tbody>
                        {phaseA.candidates.map((c) => (
                          <tr key={c.source_category_id} className="border-t">
                            <td className="p-2 font-medium">{c.source_name}</td>
                            <td className="p-2">
                              <Badge variant="secondary">{c.inferred_category || '—'}</Badge>
                            </td>
                            <td className="p-2">{c.parsed?.year || '—'}</td>
                            <td className="p-2">{c.parsed?.make || '—'}</td>
                            <td className="p-2">{c.parsed?.model || '—'}</td>
                            <td className="p-2">{c.parsed?.serial_number || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {phaseA.skipped_already_exists.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Skipping {phaseA.skipped_already_exists.length} record(s) —
                    equipment with the same name already exists.
                  </p>
                )}
              </div>

              {/* Phase B */}
              <div>
                <h3 className="text-sm font-semibold mb-2">
                  Categories to restore
                  {' '}<Badge variant="outline">{phaseB.to_create.length}</Badge>
                </h3>
                {phaseB.to_create.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    All baseline categories already present — nothing to recreate.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {phaseB.to_create.map((n) => (
                      <Badge key={n} variant="secondary" className="text-xs">{n}</Badge>
                    ))}
                  </div>
                )}
                {phaseB.already_present.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Already present: {phaseB.already_present.join(', ')}
                  </p>
                )}
              </div>

              {/* Harvested custom fields */}
              {phaseB.harvested_custom_fields_by_category
                && Object.keys(phaseB.harvested_custom_fields_by_category).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">
                    Custom fields that will be re-attached to restored categories
                  </h3>
                  <p className="text-xs text-muted-foreground mb-2">
                    These are the fields the employee entered onto the misplaced
                    equipment — we&rsquo;ll attach them back to their proper category
                    so future equipment in that category sees the same fields.
                  </p>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 text-left">
                        <tr>
                          <th className="p-2">Category</th>
                          <th className="p-2">Custom Fields</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(phaseB.harvested_custom_fields_by_category).map(([cat, fields]) => (
                          <tr key={cat} className="border-t align-top">
                            <td className="p-2 font-medium whitespace-nowrap">{cat}</td>
                            <td className="p-2">
                              <div className="flex flex-wrap gap-1">
                                {fields.map((f, i) => (
                                  <Badge key={`${cat}-${i}`} variant="outline" className="text-xs">
                                    {f.label}
                                    <span className="ml-1 text-muted-foreground">({f.field_type})</span>
                                  </Badge>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Step 3: Commit ──────────────────────────────────────── */}
      <Card data-testid="commit-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="w-5 h-5 text-green-600" />
            Step 3 · Run the recovery
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Once the preview above looks correct, click below to actually move the
            records and restore the categories. Safe to re-run if anything fails halfway.
          </p>
          <Button
            onClick={() => setConfirmRun(true)}
            disabled={!dryRun || committing}
            className="bg-green-600 hover:bg-green-700"
            data-testid="run-commit-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            {committing ? 'Running…' : 'Run Recovery Now'}
          </Button>
          {!dryRun && (
            <p className="text-xs text-amber-700 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Run the preview in Step 2 first.
            </p>
          )}

          {commitResult && (
            <div className="mt-4 border rounded-lg p-4 bg-green-50 border-green-200">
              <div className="flex items-center gap-2 text-green-800 font-semibold">
                <CheckCircle2 className="w-5 h-5" />
                Recovery finished
              </div>
              <ul className="text-sm mt-2 space-y-1">
                <li>
                  <strong>{commitResult.phase_a_migrate_equipment.migrated_count}</strong>{' '}
                  equipment record(s) moved into the Equipment page.
                </li>
                <li>
                  <strong>{commitResult.phase_b_restore_categories.created_count}</strong>{' '}
                  baseline categor(ies) restored.
                </li>
              </ul>
              <p className="text-xs text-muted-foreground mt-2">
                Re-running this button now is safe &mdash; it will show 0/0 because there&rsquo;s
                nothing left to do.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Step 4: Move stranded equipment to Assets (fix-up) ───── */}
      {stranded > 0 && (
        <Card data-testid="move-card" className="border-amber-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PackageOpen className="w-5 h-5 text-amber-600" />
              Step 4 &middot; Move stranded equipment onto the Equipment page
              <span className="ml-2 text-xs font-normal text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                {stranded} stranded
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Earlier recovery runs wrote {stranded} record(s) into the wrong
              database collection (the legacy <code className="text-xs">equipment</code> bucket).
              The Equipment page reads from a different one (<code className="text-xs">assets</code>),
              which is why those records are invisible. Click below to lift them into
              the right collection. Safe to re-run.
            </p>
            <Button onClick={runMoveDryRun} disabled={moveLoadingDry} data-testid="run-move-dryrun-btn">
              <Eye className="w-4 h-4 mr-2" />
              {moveLoadingDry ? 'Generating preview…' : 'Preview What Will Move'}
            </Button>

            {moveDry && (
              <div className="space-y-2 pt-2">
                <h3 className="text-sm font-semibold">
                  Records that will move
                  {' '}<Badge variant="outline">{moveDry.would_move_count}</Badge>
                </h3>
                {moveDry.would_move_count === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    Nothing stranded right now &mdash; all clear.
                  </p>
                ) : (
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 text-left">
                        <tr>
                          <th className="p-2">Name</th>
                          <th className="p-2">Category</th>
                          <th className="p-2">Year</th>
                          <th className="p-2">Make</th>
                          <th className="p-2">Model</th>
                          <th className="p-2">Serial</th>
                        </tr>
                      </thead>
                      <tbody>
                        {moveDry.moved.map((m) => (
                          <tr key={m.equipment_id} className="border-t">
                            <td className="p-2 font-medium">{m.name}</td>
                            <td className="p-2"><Badge variant="secondary">{m.category || '—'}</Badge></td>
                            <td className="p-2">{m.year || '—'}</td>
                            <td className="p-2">{m.make || '—'}</td>
                            <td className="p-2">{m.model || '—'}</td>
                            <td className="p-2">{m.serial_number || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {moveDry.skipped_count > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Skipping {moveDry.skipped_count} record(s) — an asset with the same
                    name already exists.
                  </p>
                )}
              </div>
            )}

            <Button
              onClick={() => setConfirmMove(true)}
              disabled={!moveDry || moveCommitting || moveDry.would_move_count === 0}
              className="bg-green-600 hover:bg-green-700"
              data-testid="run-move-commit-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              {moveCommitting ? 'Moving…' : 'Move Records Into the Equipment Page'}
            </Button>

            {moveResult && (
              <div className="mt-3 border rounded-lg p-4 bg-green-50 border-green-200">
                <div className="flex items-center gap-2 text-green-800 font-semibold">
                  <CheckCircle2 className="w-5 h-5" />
                  Move finished
                </div>
                <ul className="text-sm mt-2 space-y-1">
                  <li>
                    <strong>{moveResult.moved_count}</strong> record(s) lifted into the
                    Equipment page.
                  </li>
                  {moveResult.skipped_count > 0 && (
                    <li>
                      <strong>{moveResult.skipped_count}</strong> skipped (duplicate name).
                    </li>
                  )}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Step 5: Apply standard maintenance fields ───────────── */}
      <Card data-testid="seed-card" className="border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackageOpen className="w-5 h-5 text-blue-600" />
            Step 5 &middot; Apply standard maintenance fields to a category
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Use this when a category (like <strong>Vac Truck</strong>) is
            missing its inspection-date fields. We&rsquo;ve pre-loaded sensible
            defaults from the staging schemas (Hydro Test Date, DOT Inspection,
            Oil Change Due, Registration, etc). Pick which categories to apply
            them to and click <em>Apply</em>. Safe to re-run &mdash; fields with
            the same label are never duplicated.
          </p>
          <Button onClick={loadSeedPreview} disabled={seedLoading} variant="outline" data-testid="load-seed-preview-btn">
            <Eye className="w-4 h-4 mr-2" />
            {seedLoading ? 'Loading…' : (seedPreview ? 'Refresh List' : 'Show Categories Missing Fields')}
          </Button>

          {seedPreview && (
            <div className="space-y-3 pt-2">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs space-y-1">
                <div className="font-semibold text-slate-700">Legend</div>
                <div>
                  <Badge variant="outline" className="text-amber-700 border-amber-300 text-xs mr-2">missing fields</Badge>
                  This category currently has zero custom fields — usually safe to check.
                </div>
                <div>
                  <span className="text-xs px-1.5 py-0.5 rounded border bg-white">Field name</span>
                  <span className="ml-2">= WILL be added to this category on Apply.</span>
                </div>
                <div>
                  <span className="text-xs px-1.5 py-0.5 rounded border bg-white line-through opacity-50">Field name</span>
                  <span className="ml-2">= already present, will be skipped (no duplicate).</span>
                </div>
                <div className="text-slate-600">
                  Pre-checked rows are ones where applying actually adds something. Uncheck any row to skip it.
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-2 w-10"></th>
                      <th className="p-2">Category</th>
                      <th className="p-2">Existing Fields</th>
                      <th className="p-2">Will Add</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seedPreview.categories.filter(c => c.has_suggestion).map((c) => {
                      const existingLc = (c.current_field_labels || []).map((s) => (s || '').toLowerCase());
                      const newOnes = (c.suggested_defaults || []).filter(
                        (f) => !existingLc.includes((f.label || '').toLowerCase())
                      );
                      const allPresent = newOnes.length === 0;
                      return (
                      <tr key={c.id} className={`border-t align-top ${allPresent ? 'bg-slate-50/60' : ''}`}>
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={!!seedSelections[c.name]}
                            onChange={(e) => setSeedSelections(prev => ({ ...prev, [c.name]: e.target.checked }))}
                            data-testid={`seed-cb-${c.name.replace(/\s+/g, '-')}`}
                            disabled={allPresent}
                          />
                        </td>
                        <td className="p-2 font-medium">
                          {c.name}
                          {c.current_field_count === 0 && (
                            <Badge variant="outline" className="ml-2 text-amber-700 border-amber-300 text-xs">
                              missing fields
                            </Badge>
                          )}
                          {allPresent && (
                            <Badge variant="outline" className="ml-2 text-green-700 border-green-300 text-xs">
                              all fields already present
                            </Badge>
                          )}
                        </td>
                        <td className="p-2 text-xs text-muted-foreground">
                          {c.current_field_labels.length ? c.current_field_labels.join(', ') : <em>none</em>}
                        </td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {c.suggested_defaults.map((f, i) => {
                              const exists = existingLc.includes((f.label || '').toLowerCase());
                              return (
                                <Badge
                                  key={`${c.name}-${i}`}
                                  variant={exists ? 'secondary' : 'outline'}
                                  className={`text-xs ${exists ? 'opacity-50 line-through' : ''}`}
                                  title={exists ? 'Already present — will be skipped' : 'Will be added'}
                                >
                                  {f.label}{f.reminder_days_before != null ? ` (${f.reminder_days_before}d reminder)` : ''}
                                </Badge>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
              <Button
                onClick={applySeedFields}
                disabled={seedApplying || Object.values(seedSelections).every(v => !v)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="apply-seed-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {seedApplying ? 'Applying…' : 'Apply Maintenance Fields to Selected Categories'}
              </Button>
              {seedResult && (
                <div className="border rounded-lg p-3 bg-green-50 border-green-200 text-sm">
                  <div className="flex items-center gap-2 text-green-800 font-semibold">
                    <CheckCircle2 className="w-4 h-4" />
                    Done &mdash; {seedResult.applied_count} categor{seedResult.applied_count === 1 ? 'y' : 'ies'} updated.
                  </div>
                  <ul className="mt-2 space-y-1 text-xs">
                    {seedResult.plan.filter(p => p.status === 'apply').map(p => (
                      <li key={p.category}>
                        <strong>{p.category}</strong>: added {p.added_labels.join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Step 6: Consolidate duplicate categories (Vac Truck / Vac Trucks) ─ */}
      <Card data-testid="consolidate-card" className="border-purple-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderTree className="w-5 h-5 text-purple-600" />
            Step 6 &middot; Consolidate duplicate categories
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Merges <code className="text-xs">Vac Trucks</code> (plural, no
            fields) into <code className="text-xs">Vac Truck</code> (singular,
            with fields) &mdash; whichever has the most custom_fields wins. Any
            asset currently pointing at the loser gets re-pointed at the
            winner, and old custom-field-value UUIDs are re-mapped to the
            winner&rsquo;s field ids by label. The loser category is then deleted.
            Safe to re-run.
          </p>
          <Button onClick={runConsolDryRun} disabled={consolLoading} variant="outline" data-testid="consol-preview-btn">
            <Eye className="w-4 h-4 mr-2" />
            {consolLoading ? 'Loading…' : 'Preview Consolidation'}
          </Button>

          {consolDry && (
            <div className="space-y-2 pt-2">
              {consolDry.plan.length === 0 && (
                <p className="text-sm text-muted-foreground italic">
                  Nothing to consolidate &mdash; no duplicates found.
                </p>
              )}
              {consolDry.plan.map((p, idx) => (
                <div key={idx} className="border rounded-lg p-3 text-sm">
                  {p.status === 'skip' ? (
                    <p className="text-muted-foreground">
                      Skip: <code className="text-xs">{p.group.join(' / ')}</code> &mdash; {p.reason}
                    </p>
                  ) : (
                    <>
                      <div className="font-semibold">
                        Winner: <Badge variant="secondary">{p.winner.name}</Badge>
                        <span className="text-xs text-muted-foreground ml-2">
                          ({p.merged_field_count_after} field{p.merged_field_count_after === 1 ? '' : 's'} after merge)
                        </span>
                      </div>
                      <div className="mt-1">
                        Loser{p.losers.length === 1 ? '' : 's'} (will be deleted):{' '}
                        {p.losers.map((l, i) => (
                          <Badge key={i} variant="outline" className="ml-1 text-rose-700 border-rose-300">
                            {l.name}
                          </Badge>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">
                        Assets to re-point: <strong>{p.assets_to_repoint.length}</strong>
                      </div>
                      {p.assets_to_repoint.length > 0 && (
                        <ul className="mt-1 text-xs space-y-0.5">
                          {p.assets_to_repoint.slice(0, 8).map((a) => (
                            <li key={a.asset_id}>
                              <code>{a.asset_name}</code>: <span className="text-rose-600">{a.old_category}</span>
                              {' → '}
                              <span className="text-green-700">{a.new_category}</span>
                              {a.remapped_custom_field_values.length > 0 && (
                                <span className="text-muted-foreground ml-1">
                                  ({a.remapped_custom_field_values.length} custom-field value{a.remapped_custom_field_values.length === 1 ? '' : 's'} re-mapped)
                                </span>
                              )}
                            </li>
                          ))}
                          {p.assets_to_repoint.length > 8 && (
                            <li className="text-muted-foreground">…and {p.assets_to_repoint.length - 8} more</li>
                          )}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              ))}
              <Button
                onClick={runConsolCommit}
                disabled={consolCommitting || consolDry.plan.every(p => p.status !== 'merge')}
                className="bg-green-600 hover:bg-green-700"
                data-testid="consol-commit-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {consolCommitting ? 'Consolidating…' : 'Consolidate Now'}
              </Button>
              {consolResult && (
                <div className="border rounded-lg p-3 bg-green-50 border-green-200 text-sm mt-2">
                  <div className="flex items-center gap-2 text-green-800 font-semibold">
                    <CheckCircle2 className="w-4 h-4" />
                    Consolidated {consolResult.groups_merged} group(s).
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmRun} onOpenChange={setConfirmRun}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run equipment recovery?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move{' '}
              <strong>{phaseA?.candidates?.length || 0}</strong>{' '}
              equipment record(s) out of Equipment Categories and into the Equipment
              page, and restore{' '}
              <strong>{phaseB?.to_create?.length || 0}</strong>{' '}
              missing baseline categor(ies).
              <br /><br />
              The action is idempotent — running it twice does the same thing as
              running it once. Each migrated item keeps an audit trail
              (<code className="text-xs">_recovered_from_category_id</code>) so it
              can be traced back if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-recovery-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runCommit}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-recovery-btn"
            >
              Yes, run recovery
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmMove} onOpenChange={setConfirmMove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move stranded equipment to the Equipment page?</AlertDialogTitle>
            <AlertDialogDescription>
              This will lift{' '}
              <strong>{moveDry?.would_move_count || 0}</strong>{' '}
              record(s) out of the legacy <code className="text-xs">equipment</code>{' '}
              collection and into the <code className="text-xs">assets</code>{' '}
              collection (which is what the Equipment page actually reads).
              Each record gets a readable asset number (e.g. <code className="text-xs">VAC-XXXXXX</code>)
              and shows up immediately. The source row in the legacy collection
              is removed so each record lives in exactly one place.
              <br /><br />
              Idempotent &mdash; running twice is safe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-move-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runMoveCommit}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-move-btn"
            >
              Yes, move them
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const toneCls =
    tone === 'warn' ? 'text-amber-600' :
    tone === 'ok'   ? 'text-green-600' :
                      'text-foreground';
  return (
    <div className="border rounded-lg p-3" data-testid={testid}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold ${toneCls}`}>{value}</div>
    </div>
  );
}
